import React, { useCallback, useEffect, useState } from 'react'
import {
  Typography,
  Checkbox,
  FormControlLabel,
  TextField,
  Box,
  FormGroup,
  Divider,
  Button,
  CircularProgress,
} from '@mui/material'
import { metodoPrograma } from '../../../components/api'
import PrimeTable from '../../../components/PrimeTable'
import MarcaImagem from './MarcaImagem'
import { setError, setInfo } from '../../../components/alerts'
import { FaPrint } from 'react-icons/fa'

const CampoTabela = ({ valorInicial, onSalvar, disabled }) => {
  const [valor, setValor] = React.useState(valorInicial ?? '')

  React.useEffect(() => {
    setValor(valorInicial ?? '')
  }, [valorInicial])

  return (
    <TextField
      variant="standard"
      size="small"
      disabled={disabled}
      value={valor}
      onChange={(e) => setValor(e.target.value)}
      onBlur={() => onSalvar(valor)}
      fullWidth
    />
  )
}

export default function Questionario({
  questionario,
  setQuestionario,
  idPaciente = undefined,
  dsNome = undefined,
  onSubmit,
  disabled = false,
  duplicar = false,
}) {
  const [lista, setLista] = useState([])
  const [dadosQuestionario, setDadosQuestionario] = useState({})
  const [respostas, setRespostas] = useState({})
  const [loading, setLoading] = useState(false)
  const [loadingQuestionario, setLoadingQuestionario] = useState(false)
  const [loadingPDF, setLoadingPDF] = useState(false)

  const [respostaObrigatoria, setRespostaObrigatoria] = useState([])

  const isRespostaVazia = (tipo, valor) => {
    const isMeta = (k) => k === 'justificativa' || k.startsWith('_')
    if (tipo === 'L') return !valor || !String(valor).trim()
    if (tipo === 'M' || tipo === 'U') {
      if (!valor || typeof valor !== 'object') return true
      return !Object.entries(valor).some(([k, v]) => !isMeta(k) && v)
    }
    if (tipo === 'O') {
      if (!valor || typeof valor !== 'object') return true
      return !Object.entries(valor).some(
        ([k, v]) => !isMeta(k) && v !== undefined && v !== null,
      )
    }
    if (tipo === 'C') {
      if (!valor || typeof valor !== 'object') return true
      return !Object.entries(valor).some(
        ([k, v]) => !isMeta(k) && Array.isArray(v) && v.length > 0,
      )
    }
    if (tipo === 'T') {
      if (!valor || typeof valor !== 'object') return true
      return !Object.values(valor).some(
        (linha) =>
          linha &&
          typeof linha === 'object' &&
          Object.entries(linha).some(([k, v]) => !isMeta(k) && v),
      )
    }
    if (tipo === 'I') return !valor
    return !valor
  }

  const buscaPerguntas = useCallback(async () => {
    setLoadingQuestionario(true)
    const res = await metodoPrograma(
      'listaPerguntas',
      {
        idQuestionario: questionario.questionario.idQuestionario,
        idResposta: questionario?.resposta?.idResposta ?? undefined,
      },
      'CtrlFormularioPublico',
    )
    if (res.status) {
      setLista(res.perguntas || [])
      setDadosQuestionario(res.questionario || {})
    } else {
      setError(res.dsErro)
    }
    setLoadingQuestionario(false)
  }, [
    questionario?.questionario?.idQuestionario,
    questionario?.resposta?.idResposta,
  ])

  const handleDownloadPDF = useCallback(async () => {
    setLoadingPDF(true)
    const res = await metodoPrograma(
      'geraRelatorio',
      {
        idQuestionario: questionario.questionario.idQuestionario,
        idResposta: questionario?.resposta?.idResposta ?? undefined,
      },
      'CtrlFormularioPublico',
    )
    if (res.status) {
      const base64 = res.base64

      // remove prefixo se vier com data:...
      const cleanBase64 = base64.includes('base64,')
        ? base64.split('base64,')[1]
        : base64

      const byteCharacters = atob(cleanBase64)
      const byteNumbers = new Array(byteCharacters.length)

      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i)
      }

      const byteArray = new Uint8Array(byteNumbers)

      const blob = new Blob([byteArray], { type: 'application/pdf' })
      const url = URL.createObjectURL(blob)

      window.open(url, '_blank')
    } else {
      setError(res.dsErro)
    }
    setLoadingPDF(false)
  }, [
    questionario?.questionario?.idQuestionario,
    questionario?.resposta?.idResposta,
  ])

  const handleEnviar = useCallback(async () => {
    const tiposMap = {}
    lista.forEach((item) => {
      tiposMap[item[1]] = item[3]
    })

    const naoRespondidas = respostaObrigatoria.filter((id) => {
      const tipo = tiposMap[id]
      return isRespostaVazia(tipo, respostas[id])
    })

    if (naoRespondidas.length > 0) {
      const menorId = naoRespondidas.map(Number).sort((a, b) => a - b)[0]
      setInfo('Preencha todas as respostas obrigatórias')
      document
        .getElementById(`pergunta-${menorId}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      return
    }

    setLoading(true)
    const res = await metodoPrograma(
      'salvaResposta',
      {
        idQuestionario: questionario.questionario.idQuestionario,
        idResposta: duplicar
          ? undefined
          : (questionario?.resposta?.idResposta ?? undefined),
        idPaciente,
        dsNome,
        respostas,
      },
      'CtrlFormularioPublico',
    )
    if (res.status) {
      setQuestionario({
        questionario: res.questionario,
        resposta: res.resposta,
      })
      setDadosQuestionario(res.questionario || {})
    } else {
      setError(res.dsErro)
    }
    if (onSubmit) {
      onSubmit(res)
    }
    setLoading(false)
  }, [
    questionario,
    setQuestionario,
    idPaciente,
    dsNome,
    onSubmit,
    duplicar,
    respostas,
    lista,
    respostaObrigatoria,
  ])

  useEffect(() => {
    buscaPerguntas()
  }, [buscaPerguntas])

  const toggleOpcaoUnica = (perguntaId, opcao) => {
    setRespostas((prev) => {
      const atual = prev[perguntaId] || {}

      if (atual[opcao]) {
        return {
          ...prev,
          [perguntaId]: {},
        }
      }

      return {
        ...prev,
        [perguntaId]: {
          [opcao]: true,
        },
      }
    })
  }

  const toggleOpcao = (perguntaId, opcao) => {
    setRespostas((prev) => {
      const atual = prev[perguntaId] || {}

      return {
        ...prev,
        [perguntaId]: {
          ...atual,
          [opcao]: !atual[opcao],
        },
      }
    })
  }

  const toggleOpcaoColuna = (perguntaId, indiceOpcao, indiceColuna) => {
    setRespostas((prev) => {
      const perguntaAtual = prev[perguntaId] || {}

      return {
        ...prev,
        [perguntaId]: {
          ...perguntaAtual,
          [indiceOpcao]:
            perguntaAtual[indiceOpcao] === indiceColuna
              ? undefined // desmarca se clicar de novo
              : indiceColuna, // marca apenas UM
        },
      }
    })
  }

  const toggleOpcaoColunaMultipla = (perguntaId, linha, coluna) => {
    setRespostas((prev) => {
      const atual = prev[perguntaId] || {}
      const colunas = atual[linha] || []

      return {
        ...prev,
        [perguntaId]: {
          ...atual,
          [linha]: colunas.includes(coluna)
            ? colunas.filter((c) => c !== coluna)
            : [...colunas, coluna],
        },
      }
    })
  }

  const atualizarTextoColuna = (
    perguntaId,
    indiceOpcao,
    indiceColuna,
    valor,
  ) => {
    setRespostas((prev) => {
      const perguntaAtual = prev[perguntaId] || {}
      const linhaAtual = perguntaAtual[indiceOpcao] || {}

      // 🔥 evita recriação desnecessária
      if (linhaAtual[indiceColuna] === valor) return prev

      return {
        ...prev,
        [perguntaId]: {
          ...perguntaAtual,
          [indiceOpcao]: {
            ...linhaAtual,
            [indiceColuna]: valor,
          },
        },
      }
    })
  }

  const atualizarRespostaAberta = (perguntaId, valor) => {
    setRespostas((prev) => ({
      ...prev,
      [perguntaId]: valor,
    }))
  }

  const atualizarJustificativa = (perguntaId, valor) => {
    setRespostas((prev) => ({
      ...prev,
      [perguntaId]: {
        ...prev[perguntaId],
        justificativa: valor,
      },
    }))
  }

  useEffect(() => {
    if (!lista) return
    const obrigatorias = lista
      .filter((item) => item?.[7] === 'S')
      .map((item) => item?.[1])
    setRespostaObrigatoria(obrigatorias)
  }, [lista])

  useEffect(() => {
    if (!lista) return

    const inicial = {}

    lista.forEach((item) => {
      const id = item[1]
      const tipo = item[3]
      const resposta = item[5]?.resposta

      if (
        tipo === 'M' ||
        tipo === 'U' ||
        tipo === 'O' ||
        tipo === 'C' ||
        tipo === 'T'
      ) {
        inicial[id] = resposta || {}
      } else {
        inicial[id] = resposta || ''
      }
    })

    setRespostas(inicial)
  }, [lista])

  const componentesPorTipo = {
    L: (id) => (
      <TextField
        placeholder={disabled ? '' : 'Sua resposta'}
        InputLabelProps={{ shrink: true }}
        variant="standard"
        size="small"
        style={{ margin: 5, marginTop: 10 }}
        value={respostas[id] ?? ''}
        fullWidth
        disabled={disabled}
        onChange={(e) => atualizarRespostaAberta(id, e.target.value)}
      />
    ),

    M: (id, opcoes) => (
      <Box>
        <FormGroup style={{ margin: 5, marginTop: 1 }}>
          {opcoes.map((op, i) => (
            <Box key={i}>
              <FormControlLabel
                disabled={disabled}
                control={
                  <Checkbox
                    checked={!!respostas[id]?.[op?.[2]]}
                    onChange={() => toggleOpcao(id, op?.[2])}
                    size="small"
                    sx={{
                      '& .MuiSvgIcon-root': {
                        fontSize: 18,
                      },
                      padding: '4px',
                    }}
                  />
                }
                label={op?.[3]}
              />
            </Box>
          ))}
        </FormGroup>
      </Box>
    ),

    U: (id, opcoes) => (
      <FormGroup style={{ margin: 5, marginTop: 1 }}>
        {opcoes.map((op, i) => (
          <Box key={i}>
            <FormControlLabel
              disabled={disabled}
              control={
                <Checkbox
                  checked={!!respostas[id]?.[op?.[2]]}
                  onChange={() => toggleOpcaoUnica(id, op?.[2])}
                  size="small"
                  sx={{
                    '& .MuiSvgIcon-root': {
                      fontSize: 18,
                    },
                    padding: '4px',
                  }}
                />
              }
              label={op?.[3]}
            />
          </Box>
        ))}
      </FormGroup>
    ),

    O: (id, opcoes, colunas) => (
      <PrimeTable
        key={id}
        dadosTable={opcoes}
        disablePagination
        rows={9999}
        habSearch={false}
        head={['', ...(colunas?.map((col) => col[1]) || [])]}
        style={{ fontSize: 10 }}
        line={(item) => {
          const nrOpcao = item[2]

          return [
            item[3],
            ...colunas.map((col, index) => (
              <Checkbox
                disabled={disabled}
                key={`${id}-${nrOpcao}-${index}`}
                checked={respostas[id]?.[nrOpcao] === col[0]}
                onChange={() => toggleOpcaoColuna(id, nrOpcao, col[0])}
                size="small"
                sx={{
                  '& .MuiSvgIcon-root': {
                    fontSize: 15,
                  },
                  padding: '0px',
                }}
              />
            )),
          ]
        }}
      />
    ),
    C: (id, opcoes, colunas) => (
      <PrimeTable
        key={id}
        dadosTable={opcoes}
        disablePagination
        rows={9999}
        habSearch={false}
        head={['', ...(colunas?.map((col) => col[1]) || [])]}
        line={(item) => {
          const nrOpcao = item[2]

          return [
            item[3],
            ...colunas.map((col, index) => (
              <Checkbox
                disabled={disabled}
                key={`${id}-${nrOpcao}-${index}`}
                checked={respostas[id]?.[nrOpcao]?.includes(col[0]) || false}
                onChange={() => toggleOpcaoColunaMultipla(id, nrOpcao, col[0])}
                size="small"
                sx={{
                  '& .MuiSvgIcon-root': {
                    fontSize: 18,
                  },
                  padding: '2px',
                }}
              />
            )),
          ]
        }}
      />
    ),
    T: (id, opcoes, colunas) => (
      <PrimeTable
        key={id}
        dadosTable={opcoes}
        disablePagination
        rows={9999}
        habSearch={false}
        head={['', ...(colunas?.map((col) => col[1]) || [])]}
        line={(item) => {
          const nrOpcao = item[2]

          return [
            item[3],
            ...colunas.map((col) => (
              <CampoTabela
                key={`${id}-${nrOpcao}-${col[0]}`}
                valorInicial={respostas?.[id]?.[nrOpcao]?.[col[0]]}
                disabled={disabled}
                onSalvar={(valor) =>
                  atualizarTextoColuna(id, nrOpcao, col[0], valor)
                }
              />
            )),
          ]
        }}
      />
    ),

    I: (id, opcoes) => (
      <div key={id} style={{ marginTop: 10 }}>
        {opcoes.dsToken && (
          <MarcaImagem
            disabled={disabled}
            dsTokenArquivo={opcoes.dsToken}
            dsTokenArquivoResposta={respostas?.[id]?.dsToken}
            onUploadComplete={(v) => salvaDocumento(id, v)}
          />
        )}
      </div>
    ),
  }

  const salvaDocumento = async (id, v) => {
    setRespostas((prev) => ({
      ...prev,
      [id]: v?.documento[0],
    }))
  }

  return (
    <Box style={{ backgroundColor: '#f0ebf8', padding: 10 }}>
      {loadingQuestionario ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            mt: 10,
            mb: 10,
            gap: 5,
          }}
        >
          <CircularProgress color="inherit" />
          <span style={{ color: '#999' }}>Carregando informações...</span>
        </Box>
      ) : (
        <>
          <Box
            sx={{
              mb: 4,
              p: 1,
              borderRadius: 4,
              background: 'white',
              margin: 2,
              border: 1,
              borderColor: '#d9d3e3ff',
              borderTop: '3mm solid #007bff',
            }}
          >
            <Typography variant="h7" sx={{ fontWeight: 'bold', mb: 1 }}>
              {dadosQuestionario.dsNome}
            </Typography>
            {dadosQuestionario.dsIntroducao && (
              <>
                <Divider sx={{ mb: 2 }} />
                <Typography
                  variant="h8"
                  style={{ whiteSpace: 'pre-line' }}
                  sx={{ mb: 2 }}
                >
                  {dadosQuestionario.dsIntroducao}
                </Typography>
              </>
            )}

            <Divider sx={{ mb: 2 }} />
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              {questionario?.resposta?.idResposta && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleDownloadPDF}
                  disabled={loadingPDF}
                  startIcon={
                    loadingPDF ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <FaPrint />
                    )
                  }
                >
                  Baixar PDF
                </Button>
              )}
            </Box>
          </Box>
          {lista?.map((item) => {
            const id = item?.[1]
            const titulo = item?.[2]
            const tipo = item?.[3]
            const opcoes = item?.[4]
            const colunas = item?.[6]
            const obrigatorio = item?.[7]

            const opcoesComJust =
              opcoes?.filter?.((op) => op?.[4] === 'S') || []
            let justificativaAtiva = false
            if (opcoesComJust.length > 0) {
              if (tipo === 'O') {
                justificativaAtiva = opcoesComJust.some((op) =>
                  colunas?.some((col) => respostas[id]?.[op?.[2]] === col[0]),
                )
              } else if (tipo === 'C') {
                justificativaAtiva = opcoesComJust.some(
                  (op) => respostas[id]?.[op?.[2]]?.length > 0,
                )
              } else {
                justificativaAtiva = opcoesComJust.some(
                  (op) => !!respostas[id]?.[op?.[2]],
                )
              }
            }
            const temJustSalva = !!respostas[id]?.justificativa

            const renderComponente = componentesPorTipo[tipo]

            return (
              <Box
                key={id}
                id={`pergunta-${id}`}
                sx={{
                  mb: 1,
                  p: 1,
                  paddingTop: 1,
                  paddingBottom: 1,
                  paddingLeft: 2,
                  paddingRight: 2,
                  borderRadius: 4,
                  background: 'white',
                  marginRight: 2,
                  marginLeft: 2,
                  border: 1,
                  marginTop: 1,
                  marginBottom: 1,
                  borderColor: '#d9d3e3ff',
                }}
              >
                <Typography sx={{ fontWeight: '500', mb: 0, fontSize: 15 }}>
                  {titulo}
                </Typography>

                {renderComponente ? (
                  renderComponente(id, opcoes, colunas)
                ) : (
                  <span>{tipo} - Tipo não suportado</span>
                )}

                {(justificativaAtiva || temJustSalva) &&
                  (disabled ? (
                    temJustSalva ? (
                      <Typography
                        variant="body2"
                        style={{
                          marginTop: 10,
                          whiteSpace: 'pre-wrap',
                          fontStyle: 'italic',
                          color: '#555',
                        }}
                      >
                        Justificativa: {respostas[id].justificativa}
                      </Typography>
                    ) : null
                  ) : (
                    <TextField
                      placeholder="Justificativa"
                      InputLabelProps={{ shrink: true }}
                      variant="standard"
                      size="small"
                      style={{ marginTop: 10 }}
                      value={respostas[id]?.justificativa ?? ''}
                      fullWidth
                      onChange={(e) =>
                        atualizarJustificativa(id, e.target.value)
                      }
                    />
                  ))}

                {obrigatorio === 'S' && (
                  <span style={{ color: 'red', fontSize: 10 }}>
                    * Resposta Obrigatória
                  </span>
                )}
              </Box>
            )
          })}
          {!disabled && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                mt: 3,
                mb: 4,
              }}
            >
              <Button
                variant="contained"
                color="primary"
                onClick={handleEnviar}
                disabled={loading}
              >
                {loading ? 'Enviando...' : 'Enviar respostas'}
              </Button>
            </Box>
          )}
        </>
      )}
    </Box>
  )
}
